/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 600 lines
 * - Adding: ~6 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MONGODB_LOGGING_PHASE1
 * Date:         2025-12-20
 * Modified By:  Cursor
 *
 * Description:
 * MongoDB-based log viewer replacing file-based logs.
 * Preserves all existing features and adds enhancements: date range, auto-refresh, stats, pagination.
 *
 * Related Screens / Modules:
 * - client/src/services/mongoLogs.ts
 * - server/routes/admin/mongoLogs.js
 */

// [2026-02-07] style-extraction: moved inline styles to SCSS module
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Row, Col, Form, Button, Alert, Badge, Table, Pagination, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync, faSyncAlt, faDownload, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { notify } from "../../utils/notifications";
import styles from "./AdminLogs.module.scss";
import {
  getMongoLogs,
  getMongoLogStats,
  exportMongoLogs,
  getMongoLogById,
  ServiceType,
  LogLevel,
  MongoLogEntry,
} from "../../services/mongoLogs";
import { formatDate } from "../../utils/dateFormat";
import "./LogsPage.scss";

const LogsPage: React.FC = () => {
  // Service selection
  const [selectedService, setSelectedService] = useState<ServiceType | "all">("all");
  const [selectedLevel, setSelectedLevel] = useState<LogLevel>("all");

  // Date range
  const [dateRangePreset, setDateRangePreset] = useState<"1h" | "6h" | "24h" | "7d" | "custom">("24h");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // Search
  const [searchTerm, setSearchTerm] = useState<string>("");

  // State
  const [logs, setLogs] = useState<MongoLogEntry[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);
  const [selectedLog, setSelectedLog] = useState<MongoLogEntry | null>(null);
  const [showLogDetails, setShowLogDetails] = useState<boolean>(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalLogs, setTotalLogs] = useState<number>(0);
  const pageSize = 100;

  // Auto-refresh interval ref
  const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate date range from preset
  const getDateRange = useCallback((preset: string) => {
    const now = new Date();
    let start = new Date();
    
    switch (preset) {
      case "1h":
        start = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case "6h":
        start = new Date(now.getTime() - 6 * 60 * 60 * 1000);
        break;
      case "24h":
        start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case "7d":
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case "custom":
        return { start: startDate ? new Date(startDate) : null, end: endDate ? new Date(endDate) : null };
    }
    
    return { start, end: now };
  }, [startDate, endDate]);

  // Load logs
  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const dateRange = getDateRange(dateRangePreset);
      const params = {
        service: selectedService === "all" ? undefined : selectedService,
        level: selectedLevel === "all" ? undefined : selectedLevel,
        startDate: dateRange.start ? dateRange.start.toISOString() : undefined,
        endDate: dateRange.end ? dateRange.end.toISOString() : undefined,
        search: searchTerm.trim() || undefined,
        page: currentPage,
        limit: pageSize,
      };

      const response = await getMongoLogs(params);
      setLogs(response.data.logs);
      setTotalPages(response.data.pagination.pages);
      setTotalLogs(response.data.pagination.total);
    } catch (err: any) {
      console.error("Error loading logs:", err);
      let errorMessage = "Failed to load logs.";
      
      if (err.response) {
        const status = err.response.status;
        if (status === 401) {
          errorMessage = "Authentication required. Please log in again.";
        } else if (status === 403) {
          errorMessage = "Admin privileges required to view logs.";
        } else if (status >= 500) {
          errorMessage = `Server error: ${err.response.data?.error || err.response.data?.message || "Internal server error"}`;
        } else {
          errorMessage = err.response.data?.error || err.response.data?.message || errorMessage;
        }
      } else if (err.request) {
        errorMessage = "Network error. Please check your connection.";
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [selectedService, selectedLevel, dateRangePreset, searchTerm, currentPage, getDateRange]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const dateRange = getDateRange(dateRangePreset);
      const params = {
        service: selectedService === "all" ? undefined : selectedService,
        startDate: dateRange.start ? dateRange.start.toISOString() : undefined,
        endDate: dateRange.end ? dateRange.end.toISOString() : undefined,
      };

      const response = await getMongoLogStats(params);
      setStats(response.data);
    } catch (err) {
      console.error("Error loading stats:", err);
      // Stats failure is not critical, just log it
    }
  }, [selectedService, dateRangePreset, getDateRange]);

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshIntervalRef.current = setInterval(() => {
        loadLogs();
      }, 3000); // Refresh every 3 seconds
      
      return () => {
        if (autoRefreshIntervalRef.current) {
          clearInterval(autoRefreshIntervalRef.current);
        }
      };
    } else {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
    }
  }, [autoRefresh, loadLogs]);

  // Load logs when filters change
  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Load stats when filters change
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Get log level color
  const getLogLevelColor = (level: string): string => {
    switch (level.toLowerCase()) {
      case "error":
        return "var(--color-status-red)";
      case "warn":
      case "warning":
        return "var(--color-status-orange)";
      case "info":
        return "var(--color-status-blue)";
      case "debug":
        return "var(--color-grey-2)";
      default:
        return "var(--color-black)";
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      const dateRange = getDateRange(dateRangePreset);
      const params = {
        service: selectedService === "all" ? undefined : selectedService,
        level: selectedLevel === "all" ? undefined : selectedLevel,
        startDate: dateRange.start ? dateRange.start.toISOString() : undefined,
        endDate: dateRange.end ? dateRange.end.toISOString() : undefined,
        search: searchTerm.trim() || undefined,
        format: "json" as const,
      };

      await exportMongoLogs(params);
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.success("Logs exported successfully");
    } catch (err: any) {
      console.error("Export error:", err);
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(err, "We couldn't export the logs. Please try again");
    }
  };

  // Handle log click for details
  const handleLogClick = async (logId: string) => {
    try {
      const response = await getMongoLogById(logId);
      setSelectedLog(response.data.log);
      setShowLogDetails(true);
    } catch (err) {
      console.error("Error loading log details:", err);
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(null, "We couldn't load the log details. Please try again");
    }
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="admin-logs-page" style={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
      {/* Page header */}
      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="mb-0">System Logs</h3>
          <p className="text-muted small mb-0">MongoDB-based log viewer</p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant={autoRefresh ? "success" : "outline-secondary"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            <FontAwesomeIcon icon={autoRefresh ? faSyncAlt : faSync} className="me-1" />
            {autoRefresh ? "Auto-refresh ON" : "Auto-refresh OFF"}
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={loadLogs} disabled={loading}>
            {loading ? "Loading..." : "🔄 Refresh"}
          </Button>
          <Button variant="outline-primary" size="sm" onClick={handleExport} disabled={loading}>
            <FontAwesomeIcon icon={faDownload} className="me-1" />
            Export
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          <strong>Error:</strong> {error}
        </Alert>
      )}

      {/* Statistics Dashboard */}
      {stats && (
        <Row className="mb-3">
          <Col md={3}>
            <div className="stat-card p-3 bg-light rounded">
              <div className="text-muted small">Total Logs</div>
              <div className="h4 mb-0">{stats.total.toLocaleString()}</div>
            </div>
          </Col>
          <Col md={9}>
            <div className="stat-card p-3 bg-light rounded">
              <div className="text-muted small mb-2">By Level</div>
              <div className="d-flex gap-2 flex-wrap">
                {Object.entries(stats.byLevel || {}).map(([level, count]: [string, any]) => (
                  <Badge key={level} bg="secondary" className="me-2">
                    {level}: {count.toLocaleString()}
                  </Badge>
                ))}
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* Filters section */}
      <div className="logs-filters mb-3">
        <Row className="g-2">
          {/* Service selector */}
          <Col xs={12} md={2}>
            <Form.Label className="u-label">Service</Form.Label>
            <Form.Select
              value={selectedService}
              onChange={(e) => {
                setSelectedService(e.target.value as ServiceType | "all");
                setCurrentPage(1);
              }}
              disabled={loading}
            >
              <option value="all">All Services</option>
              <option value="server">Server</option>
              <option value="mailer">Mailer</option>
              <option value="uploader">Uploader</option>
            </Form.Select>
          </Col>

          {/* Level filter */}
          <Col xs={12} md={2}>
            <Form.Label className="u-label">Level</Form.Label>
            <Form.Select
              value={selectedLevel}
              onChange={(e) => {
                setSelectedLevel(e.target.value as LogLevel);
                setCurrentPage(1);
              }}
              disabled={loading}
            >
              <option value="all">All Levels</option>
              <option value="error">Error</option>
              <option value="warn">Warn</option>
              <option value="info">Info</option>
              <option value="debug">Debug</option>
            </Form.Select>
          </Col>

          {/* Date range preset */}
          <Col xs={12} md={2}>
            <Form.Label className="u-label">Time Range</Form.Label>
            <Form.Select
              value={dateRangePreset}
              onChange={(e) => {
                setDateRangePreset(e.target.value as any);
                setCurrentPage(1);
              }}
              disabled={loading}
            >
              <option value="1h">Last Hour</option>
              <option value="6h">Last 6 Hours</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="custom">Custom Range</option>
            </Form.Select>
          </Col>

          {/* Custom date inputs */}
          {dateRangePreset === "custom" && (
            <>
              <Col xs={12} md={2}>
                <Form.Label className="u-label">Start Date</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  disabled={loading}
                />
              </Col>
              <Col xs={12} md={2}>
                <Form.Label className="u-label">End Date</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  disabled={loading}
                />
              </Col>
            </>
          )}

          {/* Search */}
          <Col xs={12} md={dateRangePreset === "custom" ? 2 : 6}>
            <Form.Label className="u-label">Search</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search log messages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  setCurrentPage(1);
                  loadLogs();
                }
              }}
              disabled={loading}
            />
          </Col>
        </Row>
      </div>

      {/* Results count */}
      {!loading && !error && (
        <div className="mb-2 text-muted small">
          Showing {logs.length} of {totalLogs.toLocaleString()} logs
          {selectedService !== "all" && ` (${selectedService})`}
          {selectedLevel !== "all" && ` (${selectedLevel})`}
        </div>
      )}

      {/* Logs table */}
      {loading && logs.length === 0 ? (
        <div className="logs-loading text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading logs...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="logs-empty text-center py-5">
          <div className={`mb-3 ${styles.emptyStateIcon}`}>📋</div>
          <h5 className="text-muted">No logs found</h5>
          <p className="text-muted small">
            {searchTerm
              ? "Try adjusting your filters or search term."
              : "No logs available for the selected filters."}
          </p>
        </div>
      ) : (
        <>
          <div className="logs-container">
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th className={styles.colTimestamp}>Timestamp</th>
                  <th className={styles.colLevel}>Level</th>
                  <th className={styles.colService}>Service</th>
                  <th>Message</th>
                  <th className={styles.colDetails}>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log._id} className={styles.clickableRow} onClick={() => handleLogClick(log._id)}>
                    <td className="small">
                      {formatDate(log.timestamp, { includeTime: true, format: "numeric" })}
                    </td>
                    <td>
                      {/* Dynamic background color must stay inline */}
                      <Badge
                        bg="secondary"
                        style={{ backgroundColor: getLogLevelColor(log.level) }}
                      >
                        {log.level.toUpperCase()}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg="info">{log.service}</Badge>
                    </td>
                    <td className={styles.messageCell}>
                      {log.message}
                    </td>
                    <td className="text-center">
                      <Button variant="link" size="sm" onClick={(e) => {
                        e.stopPropagation();
                        handleLogClick(log._id);
                      }}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination>
                <Pagination.First
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                />
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <Pagination.Item
                      key={pageNum}
                      active={pageNum === currentPage}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </Pagination.Item>
                  );
                })}
                <Pagination.Next
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </>
      )}

      {/* Log details modal */}
      <Modal show={showLogDetails} onHide={() => setShowLogDetails(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Log Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLog && (
            <div>
              <Row className="mb-2">
                <Col md={3}><strong>Timestamp:</strong></Col>
                <Col md={9}>{formatDate(selectedLog.timestamp, { includeTime: true })}</Col>
              </Row>
              <Row className="mb-2">
                <Col md={3}><strong>Level:</strong></Col>
                <Col md={9}>
                  <Badge style={{ backgroundColor: getLogLevelColor(selectedLog.level) }}>
                    {selectedLog.level.toUpperCase()}
                  </Badge>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={3}><strong>Service:</strong></Col>
                <Col md={9}><Badge bg="info">{selectedLog.service}</Badge></Col>
              </Row>
              <Row className="mb-2">
                <Col md={3}><strong>Message:</strong></Col>
                <Col md={9} className={styles.monoText}>{selectedLog.message}</Col>
              </Row>
              {selectedLog.metadata && (
                <Row className="mb-2">
                  <Col md={3}><strong>Metadata:</strong></Col>
                  <Col md={9}>
                    <pre className={styles.preBlock}>
                      {JSON.stringify(selectedLog.metadata, null, 2)}
                    </pre>
                  </Col>
                </Row>
              )}
              <Row className="mb-2">
                <Col md={12}>
                  <strong>Full Log Entry:</strong>
                  <pre className={styles.preBlockLarge}>
                    {JSON.stringify(selectedLog, null, 2)}
                  </pre>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLogDetails(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Info Note */}
      {!loading && !error && (
        <Alert variant="info" className="mt-4 small">
          <strong>Note:</strong> Logs are stored in MongoDB with automatic retention (Errors: 90 days, Warnings: 30 days, Info: 14 days, Debug: 7 days).
        </Alert>
      )}
    </div>
  );
};

export default LogsPage;

